import { type FC } from 'react';
import LabelIcon from '@mui/icons-material/Label';
import { Box } from '@mui/material';

import { getLabelColor } from '../../pastelPalette';
import type { AppointmentLabel } from '../../types';

export interface LabelChipRowProps {
  /** Labels assigned to the appointment (from `appointment.labels[]`). */
  labels: AppointmentLabel[];
}

/**
 * Renders an appointment's labels as wrapping chips — a pastel tag icon plus the
 * label NAME — for detail surfaces (the hover popover, edit sidebar, modals).
 * Wraps to multiple lines / one chip per line when the names don't fit. The
 * compact icon-only {@link LabelIconRow} stays for calendar event blocks where
 * horizontal space is tight.
 */
export const LabelChipRow: FC<LabelChipRowProps> = ({ labels }) => {
  if (!labels || labels.length === 0) {
    return null;
  }

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, minWidth: 0 }}>
      {labels.map((label) => (
        <Box
          key={label.label_id}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            maxWidth: '100%',
            minWidth: 0,
            px: 0.75,
            py: 0.25,
            borderRadius: 1,
            bgcolor: getLabelColor(label.color),
          }}
        >
          <LabelIcon sx={{ fontSize: 14, color: '#2F344F', flexShrink: 0 }} />
          <Box
            component="span"
            sx={{
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              fontSize: 12,
              fontWeight: 600,
              color: '#2F344F',
            }}
          >
            {label.name}
          </Box>
        </Box>
      ))}
    </Box>
  );
};
